import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import { PapiDaSharedDeviationSubmitInit, PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowLeft';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { BiData, OriginalsSubmitClickBiEvent } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import FormStepsDisplay from '../common/FormStepsDisplay';
import FirstStep from './_partials/FirstStep';
import SecondStep from './_partials/SecondStep';
import MultiImageUploader from '../../common/MultiImageUploader';
import {
  createArtsAndCraftsDeviation,
  updateArtsAndCraftsDeviation,
  publishArtsAndCraftsDeviation,
} from '../../redux/actions/artsAndCrafts';
import { splashReady } from '../../redux/actions/modal';
import { ArtsAndCraftsDeviationFieldName, ArtsAndCraftsDeviationFormFields } from './types';
import { useFirstStepValidation } from './validation';
import { useDefaultValues } from './hooks/useDefaultValues';

import s from './ArtsAndCraftsDeviationPopup.scss';

const FINAL_STEP = 2;

interface Props {
  initResponse?: PapiDaSharedDeviationSubmitInit;
  onRequestClose: () => void;
}

const ArtsAndCraftsDeviationPopup: React.FC<Props> = ({ initResponse, onRequestClose }) => {
  const { t } = useTranslation();
  const deviationFromProps = initResponse?.deviation;
  const [artsAndCraftsDeviation, setArtsAndCraftsDeviation] = useState<PapiDeviation | undefined>(
    deviationFromProps
  );
  const deviationIdRef = useRef<number>(undefined);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState(1);
  const [fileMissing, setFileMissing] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);

  const isMobile = useContext(MobileContext);
  const logEvent = useBiLogger();
  const firstStepValidationSchema = useFirstStepValidation();

  const dispatchCreateArtsAndCraftsDeviation = useAction(createArtsAndCraftsDeviation);
  const dispatchUpdateArtsAndCraftsDeviation = useAction(updateArtsAndCraftsDeviation);
  const dispatchPublishArtsAndCraftsDeviation = useAction(publishArtsAndCraftsDeviation);
  const dispatchAddErrorNotification = useAction(addErrorNotification);
  const dispatchSplashReady = useAction(splashReady);

  const resolver = useMemo(
    () =>
      step === 1
        ? yupResolver<ArtsAndCraftsDeviationFormFields>(firstStepValidationSchema, {
            abortEarly: false,
          })
        : undefined,
    [step, firstStepValidationSchema]
  );

  const defaultValues = useDefaultValues(initResponse);
  const formContext = useForm<ArtsAndCraftsDeviationFormFields>({
    mode: 'onSubmit',
    resolver,
    shouldUnregister: false,
    defaultValues,
  });

  const { trigger, getValues, formState } = formContext;

  const scrollToFirstError = useCallback(() => {
    const errors = formState.errors;
    if (!errors || !scrollContainerRef.current) {
      return;
    }
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) {
      return;
    }
    const el = scrollContainerRef.current.querySelector(`[data-field="${firstKey}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [formState.errors]);

  const handleContinueClick = useCallback(async () => {
    if (step === 1) {
      const isValid = await trigger();
      const hasError = !isValid || !artsAndCraftsDeviation || artsAndCraftsDeviation.isDefaultImage;

      void logEvent(
        BiData<OriginalsSubmitClickBiEvent>({
          evid: 760,
          is_error: hasError ? 1 : 0,
          step_num: 1,
        })
      );

      if (!artsAndCraftsDeviation || artsAndCraftsDeviation.isDefaultImage) {
        setFileMissing(true);
        return;
      }
      if (!isValid) {
        scrollToFirstError();
        return;
      }

      setFileMissing(false);
      setStep(step + 1);
    }
  }, [step, trigger, artsAndCraftsDeviation, scrollToFirstError, logEvent]);

  const redirectToDeviation = useCallback((deviationId: number) => {
    window.location.assign(Url.deviationLink(deviationId));
  }, []);

  const onPublishArtsAndCraftsDeviation = useCallback(
    (updatedDeviation: PapiDeviation) =>
      dispatchPublishArtsAndCraftsDeviation({
        deviationId: updatedDeviation.deviationId,
        onSuccess: () => {
          redirectToDeviation(updatedDeviation.deviationId);
        },
      }),
    [dispatchPublishArtsAndCraftsDeviation, redirectToDeviation]
  );

  const handleSubmitClick = useCallback(() => {
    const formData = getValues();

    void logEvent(
      BiData<OriginalsSubmitClickBiEvent>({
        evid: 760,
        is_error: !artsAndCraftsDeviation ? 1 : 0,
        step_num: 2,
      })
    );

    if (!artsAndCraftsDeviation) {
      dispatchAddErrorNotification(t('artsAndCraftsDeviationPopup.error.coverImage.required'));
    } else {
      dispatchUpdateArtsAndCraftsDeviation({
        deviationId: artsAndCraftsDeviation.deviationId,
        formData,
        onSuccess: (updatedDeviation: PapiDeviation) => {
          if (!updatedDeviation.isPublished) {
            onPublishArtsAndCraftsDeviation(updatedDeviation);
          } else {
            redirectToDeviation(updatedDeviation.deviationId);
          }
        },
      });
    }
  }, [
    t,
    getValues,
    artsAndCraftsDeviation,
    onPublishArtsAndCraftsDeviation,
    dispatchUpdateArtsAndCraftsDeviation,
    dispatchAddErrorNotification,
    redirectToDeviation,
    logEvent,
  ]);

  const handleSaveDraftClick = useCallback(async () => {
    if (step === 1) {
      const isPriceValid = await trigger(ArtsAndCraftsDeviationFieldName.PRICE);
      if (!isPriceValid) {
        scrollToFirstError();
        return;
      }
    }

    const formData = getValues();
    setIsSavingDraft(true);

    if (!artsAndCraftsDeviation) {
      dispatchCreateArtsAndCraftsDeviation({
        formData,
        onSuccess: (createdDeviation: PapiDeviation) => {
          deviationIdRef.current = createdDeviation.deviationId;
          setArtsAndCraftsDeviation(createdDeviation);
          setIsSavingDraft(false);
          window.location.href = Url.studio({
            products: 'originals-unlisted',
          });
        },
        onError: () => {
          setIsSavingDraft(false);
        },
      });
    } else {
      dispatchUpdateArtsAndCraftsDeviation({
        deviationId: artsAndCraftsDeviation.deviationId,
        formData,
        onSuccess: (updatedDeviation: PapiDeviation) => {
          setArtsAndCraftsDeviation(updatedDeviation);
          setIsSavingDraft(false);
          window.location.href = Url.studio({
            products: 'originals-unlisted',
          });
        },
        onError: () => {
          setIsSavingDraft(false);
        },
      });
    }
  }, [
    trigger,
    getValues,
    artsAndCraftsDeviation,
    scrollToFirstError,
    dispatchCreateArtsAndCraftsDeviation,
    dispatchUpdateArtsAndCraftsDeviation,
    step,
  ]);

  const handleBeforeUpload = useCallback(
    async () =>
      new Promise<boolean>((resolve, reject) => {
        if (!artsAndCraftsDeviation) {
          dispatchCreateArtsAndCraftsDeviation({
            formData: {} as ArtsAndCraftsDeviationFormFields,
            onSuccess: (createdDeviation: PapiDeviation) => {
              deviationIdRef.current = createdDeviation.deviationId;
              setArtsAndCraftsDeviation(createdDeviation);
              resolve(true);
            },
            onError: (error: any) => {
              reject(error);
            },
          });
          return;
        }
        resolve(true);
      }),
    [artsAndCraftsDeviation, dispatchCreateArtsAndCraftsDeviation]
  );

  const handleAfterUpload = useCallback((deviation: PapiDeviation) => {
    setArtsAndCraftsDeviation(deviation);
  }, []);

  useEffect(() => {
    dispatchSplashReady();
  }, [dispatchSplashReady]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const submitButton = (
    <BrandedButton key="confirm" variant="brand-green" size="large" onClick={handleSubmitClick}>
      {deviationFromProps ? t('common.update') : t('common.submit')}
    </BrandedButton>
  );

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      className={s['popup']}
      contentClassName={s['content-wrapper']}
      heading={
        deviationFromProps
          ? t('artsAndCraftsDeviationPopup.heading.edit')
          : t('artsAndCraftsDeviationPopup.heading')
      }
      subheading={
        <FormStepsDisplay
          currentStep={step}
          steps={[
            t('artsAndCraftsDeviationPopup.subheading.step1'),
            t('artsAndCraftsDeviationPopup.subheading.step2'),
          ]}
        />
      }
      withScrollableContentWrapper={isMobile}
      headerClassName={s['header']}
      showHeaderBorder
      showFooterBorder
      footerLeft={[
        step > 1 && (
          <TextButton
            key="cancel"
            variant="main"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={() => setStep(step - 1)}
          >
            {t('common.back')}
          </TextButton>
        ),
      ]}
      footerRight={[
        !!artsAndCraftsDeviation?.isPublished && (
          <TextButton key="cancel" variant="main" size="large" onClick={onRequestClose}>
            {t('common.cancel')}
          </TextButton>
        ),
        !artsAndCraftsDeviation?.isPublished && (
          <TextButton
            key="draft"
            variant="main"
            size="large"
            disabled={isSavingDraft}
            onClick={handleSaveDraftClick}
          >
            {t('artsAndCraftsDeviationPopup.saveAsDraft')}
          </TextButton>
        ),
        step === FINAL_STEP ? (
          submitButton
        ) : (
          <BrandedButton
            key="confirm"
            variant="originals-angular"
            size="large"
            onClick={handleContinueClick}
          >
            {t('common.continue')}
          </BrandedButton>
        ),
      ]}
    >
      <FormProvider {...formContext}>
        <div className={cn(s['content'], s[`step-${step}`])}>
          <div className={s['main-area']} ref={scrollContainerRef}>
            {step === 1 && <FirstStep deviation={artsAndCraftsDeviation} />}
            {step === 2 && <SecondStep scrollContainerRef={scrollContainerRef} />}
          </div>
          <div className={s['preview-area']}>
            <MultiImageUploader
              variant="arts-and-crafts"
              deviation={deviationFromProps}
              deviationId={deviationFromProps ? undefined : deviationIdRef}
              className={cn(
                artsAndCraftsDeviation && !artsAndCraftsDeviation.isDefaultImage && s['preview']
              )}
              emptyStateHasError={fileMissing}
              onBeforeUpload={handleBeforeUpload}
              onAfterUpload={handleAfterUpload}
            />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
};
ArtsAndCraftsDeviationPopup.displayName = 'ArtsAndCraftsDeviationPopup';

export default ArtsAndCraftsDeviationPopup;
